import {
  WhatsAppOutlined,
  CloseCircleOutlined,
  EditOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { Descriptions, Card, Button, Table, Checkbox } from "antd";
import { useCallback, useContext, useEffect, useState } from "react";
import { TicketContext } from "../../Shared/contexts/TicketContext";
import {
  checkListStatusTitles,
  updateClientAddress,
  updateClientInfo,
} from "../../../utils";
import Description from "./Description";
import { DescriptionFormInput } from "./DescriptionFormInput";
import { useSelector } from "react-redux";
import { AuthenticationState } from "../../../interfaces/users";
import { DescriptionItem } from "./DescriptionItem";
import {
  formatPhoneNumber,
  hasMinimumPermissions,
  isSharedComponentDisabled,
} from "../../../utils/helpers/userHelpers";
import { useQuery, useQueryClient } from "react-query";
import { api } from "../../../config";
import { formatCurrency, saleStatusDict } from "../../../utils/helpers/ticketHelpers";
import { TicketViewModel } from "../../../interfaces";
import Schedules from "../../Checklist/TicketModal/Tabs/ScheduleHistory/Schedules";
import { toast } from "react-toastify";

interface DetailInterfaceProps {
  ticketInfo?: TicketViewModel;
  type?: "measuring" | "checklist" | "assemble";
  disabled?: boolean;
}

const defaultClientInfoForm = {
  name: "",
  phoneNumber: "",
  alternativePhoneNumber: "",
};

const defaultForm = {
  street: "",
  add: "",
  neighbor: "",
  city: "",
  state: "",
  zipCode: "",
};

function Details({
  ticketInfo,
  type = "checklist",
  disabled = false,
}: DetailInterfaceProps) {
  const [editing, setEditing] = useState(false);
  const [editingClient, setEditingClient] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fullPromissoryLoading, setFullPromissoryLoading] = useState(false);
  const [formData, setFormData] = useState(defaultForm);
  const [clientFormData, setClientFormData] = useState(defaultClientInfoForm);
  const ticketContext = useContext(TicketContext) as any;
  const ticket: TicketViewModel = ticketInfo ? ticketInfo : ticketContext.ticket;
  const { token, user } = useSelector((state: AuthenticationState) => state);
  const queryClient = useQueryClient();
  const { refetch } = useQuery(
    [type === "checklist" ? "tickets" : "tickets/measuring"],
    () => api(token).get(`ticket/${type}/${ticket.id}`),
    { refetchOnMount: false }
  );
  const handleOnFormChange = ({ target }: any) => {
    const { id, value } = target;
    setFormData({ ...formData, [id]: value });
  };

  const setInitialFormData = useCallback(() => {
    setFormData({ ...ticket.client.address });
    setClientFormData({
      name: ticket.client.name,
      phoneNumber: formatPhoneNumber(ticket.client.phoneNumber ?? ''),
      alternativePhoneNumber: formatPhoneNumber(
        ticket.client.alternativePhoneNumber ?? ''
      ),
    });
    setEditing(false);
    setEditingClient(false);
  }, [ticket.client]);

  const handleAddressSave = async () => {
    setLoading(true);
    await updateClientAddress(token, ticket.id, formData, type);
    setEditing(false);
    setLoading(false);
    refetch();
  };

  const handleClientInfoSave = async () => {
    setLoading(true);
    await updateClientInfo(token, ticket.id, clientFormData, type);
    setEditing(false);
    setLoading(false);
    refetch();
  };

  const handleFullPromissoryChange = async (checked: boolean) => {
    try {
      setFullPromissoryLoading(true);
      await api(token).put(`ticket/checklist/${ticket.id}`, {
        isFullPromissory: checked,
      });
      await refetch();
      queryClient.invalidateQueries(["tickets/checklist/board"]);
      toast.success("Ticket atualizado com sucesso");
    } catch (error) {
      toast.error("Ocorreu um erro ao atualizar o tipo de promissória");
    } finally {
      setFullPromissoryLoading(false);
    }
  };

  useEffect(() => {
    setInitialFormData();
  }, [setInitialFormData]);

  const handleClientFormChange = (e: any) => {
    let { id, value } = e.target;
    setClientFormData({ ...clientFormData, [id]: value });
  };
  const canEditFullPromissory =
    type === "checklist" && hasMinimumPermissions("checklist", user);

  return (
    <div className="overflow-auto max-h-[60vh] mb-3">
      <Schedules />

      <Description type={type} disabled={disabled} ticketInfo={ticketInfo} />
      {!ticketInfo && (
        <>
          <Card
            className="mb-3"
            title="Venda"
            size="small"
            headStyle={{ color: "#1677FF" }}
          >
            <Descriptions size="small">
              <Descriptions.Item label="Fluxo">{"Checklist"}</Descriptions.Item>
              <Descriptions.Item label="Situação">
                {checkListStatusTitles[ticket.status]}
              </Descriptions.Item>
              <Descriptions.Item label="Vendedor">
                {ticket.seller ?? ticket.owner.fullName}
              </Descriptions.Item>
              <Descriptions.Item label="Código da Venda">
                {ticket.saleId ?? "Não vinculado"}
              </Descriptions.Item>
              <Descriptions.Item label="Código do Ticket">
                {ticket.externalId ?? "Não vinculado"}
              </Descriptions.Item>
              <Descriptions.Item label="Código Local">
                {ticket.id}
              </Descriptions.Item>
              <Descriptions.Item label="Valor Total Bruto">
                {formatCurrency(ticket.totalValue ?? 0)}
              </Descriptions.Item>

              {!!ticket.discountPercentage && (
                <>
                  <Descriptions.Item label="Valor Total Negociado">
                    {formatCurrency(
                      Number(
                        ticket.totalValue ??
                          "".replace(".", "").replace(",", ".")
                      ) *
                        (1 - (ticket.discountPercentage ?? 100) / 100)
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Percentual Desconto">
                    {ticket.discountPercentage ?? 0} %
                  </Descriptions.Item>
                </>
              )}
              {!!ticket.saleStatus && (
                <Descriptions.Item label="Situação da Venda">
                  {saleStatusDict(ticket.saleStatus ?? 0)}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Tipo de Promissória">
                <Checkbox
                  checked={!!ticket.isFullPromissory}
                  disabled={!canEditFullPromissory || fullPromissoryLoading}
                  onChange={(event) =>
                    handleFullPromissoryChange(event.target.checked)
                  }
                >
                  100% Promissória
                </Checkbox>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </>
      )}
      {type === "assemble" && (ticket as any).scheduledBy && (
        <Card
          className="mb-3"
          title="Agendamento"
          size="small"
          headStyle={{ color: "#1677FF" }}
        >
          <Descriptions size="small">
            <Descriptions.Item label="Agendado por">
              <b>{(ticket as any).scheduledBy}</b>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}
    {type === 'checklist' && (
      <Card title="Produtos" size="small" headStyle={{ color: "#1677FF" }}>
        <Table
          pagination={false}
          columns={[
            {
              title: "Nome",
              key: "name",
              dataIndex: "name",
            },
            {
              title: "Vlr Unit",
              key: "unitValue",
              dataIndex: "unitValue",
              render: (value) => formatCurrency(value),
            },
            {
              title: "Qtd",
              key: "qtd",
              dataIndex: "qtd",
            },

            {
              title: "Vlr Total",
              key: "totalValue",
              dataIndex: "totalValue",
              render: (value) => formatCurrency(value),
            },

            {
              title: "Vlr Negociado",
              key: "totalValue2",
              dataIndex: "totalValue",
              render: (value) => {
                const realValue =
                  value * (1 - (ticket.discountPercentage ?? 100) / 100);
                return formatCurrency(realValue);
              },
            },
          ]}
          dataSource={ticket.saleProducts}
        />
      </Card>
    )}


      <Card title="Cliente" size="small" headStyle={{ color: "#1677FF" }}>
        <div className="relative grid grid-cols-3 gap-4">
          <DescriptionFormInput
            onChange={handleClientFormChange}
            id="name"
            value={clientFormData.name}
            label="Nome:"
            editing={editingClient}
          />

          <DescriptionFormInput
            onChange={handleClientFormChange}
            value={formatPhoneNumber(clientFormData.phoneNumber)}
            id="phoneNumber"
            label="Contato:"
            editing={editingClient}
          >
            <a
              href={`https://dashboard.firefly.chat/chatOnline/externalChatChannel/cmdoysvr90063ik0iat2dz0ur?clientPhone=55${formatPhoneNumber(
                ticket.client.phoneNumber ?? ""
              )?.replace(
                /\D+/g,
                ""
              )}&clientName=${ticket.client.name}&showMessagesOnly=true`}
              target="_blank"
              rel="noreferrer"
            >
              <WhatsAppOutlined className="mr-1" />
              {formatPhoneNumber(ticket.client.phoneNumber ?? "")}
            </a>
          </DescriptionFormInput>

          <DescriptionFormInput
            onChange={handleClientFormChange}
            id="alternativePhoneNumber"
            value={formatPhoneNumber(clientFormData.alternativePhoneNumber)}
            label="Contato Alternativo:"
            editing={editingClient}
          >
            <a
              href={`https://dashboard.firefly.chat/chatOnline/externalChatChannel/cmdoysvr90063ik0iat2dz0ur?clientPhone=55${formatPhoneNumber(
                ticket.client.alternativePhoneNumber ?? ""
              )?.replace(
                /\D+/g,
                ""
              )}&clientName=${ticket.client.name}&showMessagesOnly=true`}
              target="_blank"
              rel="noreferrer"
            >
              <WhatsAppOutlined className="mr-1" />
              {formatPhoneNumber(ticket.client.alternativePhoneNumber ?? "")}
            </a>
          </DescriptionFormInput>
          <DescriptionItem label="CPF/CNPJ:">
            {ticket.client.taxId}
          </DescriptionItem>
          <DescriptionItem label="E-mail:">
            {ticket.client?.email}
          </DescriptionItem>

          <div className="w-full flex items-end justify-end">
            {editingClient ? (
              <div className="flex gap-2">
                <Button
                  className="mt-4"
                  type="link"
                  danger
                  onClick={setInitialFormData}
                  loading={loading}
                  icon={<CloseCircleOutlined />}
                />
                <Button
                  className="mt-4"
                  type="link"
                  onClick={handleClientInfoSave}
                  loading={loading}
                  icon={<SaveOutlined />}
                />
              </div>
            ) : (
              <Button
                disabled={isSharedComponentDisabled(type, user, disabled)}
                type="link"
                onClick={() => setEditingClient(true)}
                icon={<EditOutlined />}
              />
            )}
          </div>

          <DescriptionFormInput
            onChange={handleOnFormChange}
            id="street"
            value={formData.street}
            label="Endereço:"
            labelClass="mt-1"
            editing={editing}
          />

          <DescriptionFormInput
            onChange={handleOnFormChange}
            id="add"
            value={formData.add}
            label="Complemento:"
            labelClass="mt-1"
            editing={editing}
          />

          <DescriptionFormInput
            onChange={handleOnFormChange}
            id="neighbor"
            value={formData.neighbor}
            label="Bairro:"
            labelClass="mt-1"
            editing={editing}
          />

          <DescriptionFormInput
            onChange={handleOnFormChange}
            id="city"
            value={formData.city}
            label="Cidade:"
            editing={editing}
          />

          <DescriptionFormInput
            onChange={handleOnFormChange}
            id="state"
            value={formData.state}
            label="Estado:"
            editing={editing}
          />

          <DescriptionFormInput
            onChange={handleOnFormChange}
            id="zipCode"
            value={formData.zipCode}
            label="CEP:"
            editing={editing}
          />
        </div>
        <div className="w-full flex items-end justify-end">
          {editing ? (
            <div className="flex gap-2">
              <Button
                className="mt-4"
                type="link"
                danger
                onClick={setInitialFormData}
                loading={loading}
                icon={<CloseCircleOutlined />}
              />
              <Button
                className="mt-4"
                type="link"
                onClick={handleAddressSave}
                loading={loading}
                icon={<SaveOutlined />}
              />
            </div>
          ) : (
            <Button
              disabled={isSharedComponentDisabled(type, user, disabled)}
              type="link"
              onClick={() => setEditing(true)}
              icon={<EditOutlined />}
            />
          )}
        </div>
      </Card>
    </div>
  );
}

export default Details;
